import {defineStore} from 'pinia';
import {computed, inject, ref, useAuth, useInfosRequest, useTranslation} from '#imports';
import {useErrorHandler} from '@malt/utils';
import type {FetchError} from '@api';
import {Configuration, ExpertiseApi, type PostalAddress} from '@api';
import {RegisteredRepositories} from '~/config/application.services';
import {expertiseApiMapper} from '~/features/expertises/helpers/ExpertiseApiMapper';
import {initFetchErrors, type ProfileFetchErrors} from '~/store/profileFetchErrors';
import type {MinMaxRuleResource, ProfileCompletionCheckListResource, UpdateSeoUrlRequest} from '~/types/api-raw-types';
import type {Appraisal, Appraisals} from '~/types/domain/Appraisals';
import type {Breadcrumb} from '~/types/domain/Breadcrumb';
import type {CategorizationCategory, CategorizationFamily} from '~/types/domain/Categorization';
import type {Certifications} from '~/types/domain/Certifications';
import type {CompanySuggestions} from '~/types/domain/CompanySuggestions';
import type {DevDetails} from '~/types/domain/DevDetails';
import type {Educations} from '~/types/domain/Educations';
import type {Experiences} from '~/types/domain/Experiences';
import type {FreelanceCharter} from '~/types/domain/FreelanceCharter';
import type {LanguageLevel} from '~/types/domain/Languages';
import {Languages} from '~/types/domain/Languages';
import type {LocationPreferences} from '~/types/domain/LocationPreferences';
import type {Portfolio} from '~/types/domain/Portfolio';
import type {ExperienceLevel, Profile} from '~/types/domain/Profile';
import type {ProfileIndustryExpertise} from '~/types/domain/ProfileExpertises';
import type {ProfileStoreRequest} from '~/types/domain/ProfileStoreRequest';
import type {SectionsVisibilityResource} from '~/types/domain/SectionsVisibility';
import type {SimilarProfile} from '~/types/domain/SimilarProfile';
import type {FunctionalScopesAvailable} from '~/types/domain/skill/Functional';
import type {IndustryExpertisesAvailable, UpdateIndustryExpertiseRequest} from '~/types/domain/skill/Industry';
import {MinMaxRule} from '~/types/domain/ValidationRule';
import type {VerificationChecklist} from '~/types/domain/VerificationChecklist';
import type {Visibility} from '~/types/domain/Visibility';
import type {UpdateCertificationRequest} from '~/types/infra/CertificationRequest';
import type {UpdateEducationRequest} from '~/types/infra/EducationRequest';
import type {ExperienceRequest} from '~/types/infra/ExperienceRequest';
import type {ProfileRelationsResource} from '~/types/infra/repository/ProfileRepositoryAPI';
import type {PriceStat} from '~/types/PriceStat';
import {profileRelationsDisplayMapper, type ProfileRelationsDisplay} from './profileRelations/profileRelationsDisplayMapper';
import {useSkillSetStore} from '@/store/skill-set/skillSetStore';

export type UpdatePriceRequest = {
    price: number;
    currencyCode: string;
    priceHidden: boolean;
};

export interface UpdateBreadcrumbRequest {
    family: string;
    category?: string;
    job?: string;
}

export interface LanguageRequest {
    code: string;
    name: string;
}

export interface LanguageAndLevelRequest {
    language: LanguageRequest;
    level: LanguageLevel;
}

export interface UpdateLanguageRequest {
    languages: LanguageAndLevelRequest[];
}

export type InvitationsRequest = {
    emails: string;
    message: string;
    experienceId: string | null;
};

export interface Skill {
    label: string;
    occurences?: number;
}

export interface UpdateActivityLocationsRequest {
    address: PostalAddress;
    addressRadius: number;
    remoteOnly: boolean;
    activityLocations: {address: PostalAddress | null; radius: number; serverError?: string}[];
    willingnessToTravelInternationally?: boolean;
}

export interface LocationPreferenceValidationError {
    type: 'INVALID_ADDRESS' | 'ACTIVITY_LOCATION_IS_TOO_FAR' | 'INVALID_ACTIVITY_LOCATION_ADDRESS';
    index: number | null;
}

export interface ActivityLocationWithLabel {
    address: PostalAddress;
    radius?: number;
    label: string;
}

export interface HotReloadedProfileLocation {
    fromSearch: boolean;
    remoteOnly: boolean;
}

export interface UpdateActivityLocationsResource {
    address: PostalAddress;
    addressRadius: number;
    remoteOnly: boolean;
    unlimitedActivityLocation: boolean;
    activityLocations: ActivityLocationWithLabel[];
    validationErrors: LocationPreferenceValidationError[];
    hotReloadedProfileLocation: HotReloadedProfileLocation;
}

export const useProfileStore = defineStore('profile', () => {
    const portfolioRepository = inject(RegisteredRepositories.injectionKeys.portfolio)!;
    const profileRepository = inject(RegisteredRepositories.injectionKeys.profile)!;
    const apiConfiguration = new Configuration({basePath: ''});
    const expertiseApi = new ExpertiseApi(apiConfiguration);

    const {handleFetchError} = useErrorHandler();
    const {t} = useTranslation();

    const profile = ref<Profile>();
    const breadcrumb = ref<Breadcrumb>();
    const devDetails = ref<DevDetails>();
    const freelanceCharter = ref<FreelanceCharter>();
    const experiences = ref<Experiences>();
    const educations = ref<Educations>();
    const certifications = ref<Certifications>();
    const industryExpertises = ref<ProfileIndustryExpertise[]>();
    const visibility = ref<Visibility>();
    const portfolio = ref<Portfolio>();
    const sectionsVisibility = ref<SectionsVisibilityResource>();
    const verificationChecklist = ref<VerificationChecklist>();
    const industryExpertiseRule = ref<MinMaxRuleResource>();
    const relations = ref<ProfileRelationsResource>();
    const relationsDisplay = ref<ProfileRelationsDisplay>();
    const averagePriceCurrentFreelancer = ref<PriceStat>();
    const profileFetchErrors = ref<ProfileFetchErrors>(initFetchErrors());
    const appraisals = ref<Appraisals>();

    const profileLocationPreferences = computed((): LocationPreferences | undefined => profile.value?.locationPreferences);

    async function getProfile(request: ProfileStoreRequest): Promise<Profile> {
        profile.value = await profileRepository.fetchProfile(
            request.details.route.profileIdOrAlias,
            request.details.registry,
            request.details.route.queryParameters || '',
        );

        return profile.value;
    }

    async function getRelations(): Promise<void> {
        const rawRelations = await profileRepository.fetchRelations(profile.value!);
        relations.value = rawRelations;
        relationsDisplay.value = profileRelationsDisplayMapper(rawRelations);
    }

    async function getVisibility() {
        visibility.value = await profileRepository.fetchVisibility(profile.value!);
    }

    async function getAppraisals(): Promise<Appraisals | undefined> {
        appraisals.value = await profileRepository.fetchAppraisals(profile.value!);
        return appraisals.value;
    }

    async function updateAppraisalVisibility(appraisal: Appraisal) {
        const {user} = useAuth();
        const isClientAppraisal = appraisal.clientAccountId && appraisal.clientAccountId === user?.id;
        const visibilityParam = isClientAppraisal ? appraisal.hiddenByClient : appraisal.hidden;
        await profileRepository.updateAppraisalVisibility(appraisal, visibilityParam);
        if (isClientAppraisal) {
            appraisal.hiddenByClient = !appraisal.hiddenByClient;
        } else {
            appraisal.hidden = !appraisal.hidden;
        }
    }

    function setAppraisals(newAppraisals: Appraisals | undefined) {
        appraisals.value = newAppraisals;
    }

    async function getFreelanceCharter() {
        freelanceCharter.value = await profileRepository.fetchFreelanceCharter(profile.value!);
    }

    async function getPortfolio(): Promise<Portfolio | undefined> {
        portfolio.value = await portfolioRepository.fetchPortfolio(profile.value!);

        return portfolio.value;
    }

    async function getBreadcrumb(): Promise<Breadcrumb | undefined> {
        breadcrumb.value = await profileRepository.fetchBreadcrumb(profile.value!);

        return breadcrumb.value;
    }

    async function getDevDetails() {
        devDetails.value = await profileRepository.fetchDevDetails(profile.value!);
    }

    async function getAveragePriceCurrentFreelancer() {
        averagePriceCurrentFreelancer.value = await profileRepository.fetchAveragePriceCurrentFreelancer(profile.value!);
    }

    async function reloadEducations(): Promise<Educations | undefined> {
        educations.value = await profileRepository.fetchEducations(profile.value!);

        return educations.value;
    }

    async function reloadCertifications(): Promise<Certifications | undefined> {
        certifications.value = await profileRepository.fetchCertifications(profile.value!);

        return certifications.value;
    }

    async function reloadExperiences(): Promise<Experiences | undefined> {
        experiences.value = await profileRepository.fetchExperiences(profile.value!);

        return experiences.value;
    }

    async function reloadIndustryExpertises(): Promise<ProfileIndustryExpertise[] | undefined> {
        try {
            const profileExpertises = await profileRepository.fetchProfileExpertises(profile.value!);
            industryExpertises.value = profileExpertises.industryExpertises;
            profileFetchErrors.value.expertises = false;
        } catch (e) {
            industryExpertises.value = [];
            profileFetchErrors.value.expertises = true;
        }

        return industryExpertises.value;
    }

    async function getSectionsVisibility(params: string | null = null) {
        sectionsVisibility.value = await profileRepository.fetchSectionsVisibility(profile.value!, params);

        return sectionsVisibility.value;
    }

    async function getVerificationChecklist(): Promise<VerificationChecklist | undefined> {
        verificationChecklist.value = await profileRepository.fetchVerificationChecklist(profile.value!);

        return verificationChecklist.value;
    }

    async function updateHeadline(headline: string) {
        await profileRepository.updateHeadline(profile.value!, headline);
        profile.value!.headline = headline;
    }

    async function updateDisplayOption(option: string) {
        await profileRepository.updateDisplayOption(profile.value!, option);
        profile.value!.privacy.nameDisplayOption = option;
    }

    async function updateExperienceLevel(experienceLevel: ExperienceLevel) {
        await profileRepository.updateExperienceLevel(profile.value!, experienceLevel);
        profile.value!.experienceLevel = experienceLevel;
    }

    async function updatePrice(request: UpdatePriceRequest) {
        await profileRepository.updatePrice(profile.value!, request);
        profile.value!.price.price = request.price;
        profile.value!.price.currencyCode = request.currencyCode;
        profile.value!.price.priceHidden = request.priceHidden;
    }

    async function updateDescription(description: string) {
        await profileRepository.updateDescription(profile.value!, description);
        profile.value!.description = description;
    }

    async function updateExperience(request: ExperienceRequest) {
        await profileRepository.updateExperience(profile.value!, request);
        await reloadExperiences();
        const skillSetStore = useSkillSetStore();
        await skillSetStore.fetchSkillSet();
    }

    async function reorderExperiences(request: string[]) {
        try {
            await profileRepository.reorderExperiences(profile.value!, request);
            await reloadExperiences();
        } catch (e) {
            handleFetchError(e as FetchError, {customErrorMessage: t('generic.error.unexpected')});
        }
    }

    async function addExperience(request: ExperienceRequest) {
        await profileRepository.addExperience(profile.value!, request);
        await reloadExperiences();
        const skillSetStore = useSkillSetStore();
        await skillSetStore.fetchSkillSet();
    }

    async function deleteExperience(experienceId: string) {
        await profileRepository.deleteExperience(profile.value!, experienceId);
        await reloadExperiences();
    }

    async function updateEducations(request: UpdateEducationRequest) {
        await profileRepository.updateEducations(profile.value!, request);
        await reloadEducations();
    }

    async function updateCertifications(request: UpdateCertificationRequest) {
        await profileRepository.updateCertifications(profile.value!, request);
        await reloadCertifications();
    }

    async function uploadPhoto(file: File): Promise<any> {
        await profileRepository.uploadPhoto(profile.value!, file);
        await reloadPhoto();
    }

    async function reloadPhoto() {
        profile.value!.photo = await profileRepository.reloadPhoto(profile.value!);
    }

    async function updateBreadcrumb(breadcrumbRequest: UpdateBreadcrumbRequest) {
        await profileRepository.updateBreadcrumb(profile.value!, breadcrumbRequest);
        await getBreadcrumb();
    }

    async function resendAccountValidationEmail() {
        await profileRepository.resendEmailValidation(profile.value!);
    }

    async function updateLanguages(request: UpdateLanguageRequest) {
        await profileRepository.updateLanguages(profile.value!, request);
        profile.value!.languages = Languages.fromRequest(request);
    }

    async function autocompleteSkills(query: string): Promise<Skill[]> {
        return await profileRepository.autocompleteSkills(profile.value!, query);
    }

    async function autocompleteCompanies(query: string): Promise<CompanySuggestions> {
        return await profileRepository.autocompleteCompanies(profile.value!, query);
    }

    async function listAllFunctionalScopes(): Promise<FunctionalScopesAvailable> {
        return await profileRepository.fetchFunctionalScopes(profile.value!);
    }

    async function listAllIndustryExpertise(): Promise<IndustryExpertisesAvailable> {
        return await profileRepository.fetchIndustryExpertises(profile.value!);
    }

    async function getIndustryExpertiseRules(): Promise<MinMaxRule> {
        if (!industryExpertiseRule.value) {
            industryExpertiseRule.value = await profileRepository.fetchIndustryExpertisesRule(profile.value!);
        }
        return new MinMaxRule(industryExpertiseRule.value);
    }

    async function updateIndustryExpertise(request: UpdateIndustryExpertiseRequest) {
        await expertiseApi.updateIndustryExpertises(expertiseApiMapper.toUpdateIndustryExpertisesRequestApi(request));
        await reloadIndustryExpertises();
    }

    const displayName = computed(() => {
        return displayNameWithOption(profile.value?.privacy.nameDisplayOption ?? '');
    });

    function displayNameWithOption(option: string) {
        if (option === 'INITIAL') {
            return `${profile.value?.firstname} ${profile.value?.lastname.charAt(0).toUpperCase()}.`;
        }

        if (option === 'INITIALS') {
            return `${profile.value?.firstname.charAt(0).toUpperCase()}. ${profile.value?.lastname.charAt(0).toUpperCase()}.`;
        }

        return `${profile.value?.firstname} ${profile.value?.lastname}`;
    }

    function updateActivityLocations(payload: UpdateActivityLocationsRequest) {
        return profileRepository.updateActivityLocations(profile.value!, payload);
    }

    function sendRecommendationRequest(payload: InvitationsRequest): Promise<any> {
        return profileRepository.sendRecommendationRequest(profile.value!, payload);
    }

    async function updateSeoUrl(payload: UpdateSeoUrlRequest): Promise<any> {
        await profileRepository.updateSeoUrl(profile.value!, payload);
        profile.value!.seoAlias = payload.url.split('/').pop()!;
        profile.value!.profileUrl = payload.url;
        profile.value!.profileCanonicalUrl = payload.url;
    }

    const profilePreferredFamily = computed((): CategorizationFamily | undefined => {
        const preferredFamily = profile.value?.categorization.families.filter(
            (family: CategorizationFamily) => family.id === profile.value?.categorization.preferredFamily,
        );

        return preferredFamily && preferredFamily.length > 0 ? preferredFamily[0] : undefined;
    });
    const profilePreferredCategory = computed((): CategorizationCategory | undefined => {
        const preferredCategory = profilePreferredFamily.value?.categories?.filter(
            (category: CategorizationCategory) => category.id === profile.value?.categorization.preferredCategory,
        );

        return preferredCategory && preferredCategory.length > 0 ? preferredCategory[0] : undefined;
    });

    const isSuperMalter = computed(() => profile.value?.badge?.includes('SUPER_MALTER') ?? false);
    const superMalterLevel = computed<number>(() => {
        switch (profile.value?.badge) {
            case 'SUPER_MALTER_L1':
                return 1;
            case 'SUPER_MALTER_L2':
                return 2;
            case 'SUPER_MALTER_L3':
                return 3;
            default:
                return 0;
        }
    });

    function setCertifications(newCertifications: Certifications | undefined) {
        certifications.value = newCertifications;
    }

    function setEducations(newEducations: Educations | undefined) {
        educations.value = newEducations;
    }

    function setExperiences(newExperiences: Experiences | undefined) {
        experiences.value = newExperiences;
    }

    function setProfile(newProfile: Profile | undefined) {
        profile.value = newProfile;
    }

    return {
        isSuperMalter,
        superMalterLevel,
        averagePriceCurrentFreelancer,
        profileLocationPreferences,
        breadcrumb,
        devDetails,
        freelanceCharter,
        experiences,
        educations,
        certifications,
        industryExpertises,
        portfolio,
        profile,
        relations,
        relationsDisplay,
        visibility,
        sectionsVisibility,
        verificationChecklist,
        getProfile,
        getVisibility,
        getRelations,
        getFreelanceCharter,
        getPortfolio,
        getDevDetails,
        getBreadcrumb,
        getVerificationChecklist,
        getSectionsVisibility,
        getAveragePriceCurrentFreelancer,
        profilePreferredCategory,
        profilePreferredFamily,
        reloadExperiences,
        reloadEducations,
        reloadCertifications,
        reloadIndustryExpertises,
        profileFetchErrors,
        displayName,
        displayNameWithOption,
        updateHeadline,
        updateDisplayOption,
        updateExperienceLevel,
        updatePrice,
        updateDescription,
        updateEducations,
        updateCertifications,
        uploadPhoto,
        reloadPhoto,
        updateBreadcrumb,
        resendAccountValidationEmail,
        sendRecommendationRequest,
        setCertifications,
        setEducations,
        setExperiences,
        setProfile,
        updateLanguages,
        autocompleteSkills,
        autocompleteCompanies,
        updateActivityLocations,
        updateExperience,
        reorderExperiences,
        addExperience,
        deleteExperience,
        updateSeoUrl,
        listAllFunctionalScopes,
        listAllIndustryExpertise,
        updateIndustryExpertise,
        getIndustryExpertiseRules,
        appraisals,
        getAppraisals,
        setAppraisals,
        updateAppraisalVisibility,
    };
});
